import { BoxProps, HStack, IconButton, Select, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuChevronFirst, LuChevronLast, LuChevronLeft, LuChevronRight } from 'react-icons/lu';

type Props = {
  page: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (page: number) => void;
  totalPageCount: number;
  disabled?: boolean;
  firstIndex?: number;
} & BoxProps;

export const Pagination = ({
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  totalPageCount,
  disabled,
  firstIndex = 1,
  ...boxProps
}: Props) => {
  const { t } = useTranslation();
  return (
    <HStack py="2" gap="12" alignItems="baseline" justifyContent="end" {...boxProps}>
      <HStack>
        <Select size="sm" value={rowsPerPage} onChange={(e) => onRowsPerPageChange(parseInt(e.target.value))} disabled={disabled}>
          {[5, 10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>
              {t('components:table.show_count', { count: size })}
            </option>
          ))}
        </Select>
      </HStack>

      <Text color="muted" fontSize="sm">
        {t(
          'components:table.pagination.items',
          totalPageCount === 0
            ? {
                from: 0,
                to: 0,
                total: 0,
              }
            : {
                from: (page - firstIndex) * rowsPerPage + firstIndex,
                to: Math.min(page * rowsPerPage, totalPageCount * rowsPerPage),
                total: totalPageCount * rowsPerPage,
              },
        )}
      </Text>

      <HStack>
        <IconButton
          icon={<LuChevronFirst />}
          aria-label={t('components:table.first')}
          onClick={() => onPageChange(firstIndex)}
          disabled={disabled || page <= firstIndex}
        />
        <IconButton
          icon={<LuChevronLeft />}
          aria-label={t('components:table.previous')}
          onClick={() => onPageChange(page - 1)}
          disabled={disabled || page <= firstIndex}
        />
        <IconButton
          icon={<LuChevronRight />}
          aria-label={t('components:table.next')}
          onClick={() => onPageChange(page + 1)}
          disabled={disabled || page >= totalPageCount}
        />
        <IconButton
          icon={<LuChevronLast />}
          aria-label={t('components:table.last')}
          onClick={() => onPageChange(totalPageCount)}
          disabled={disabled || page >= totalPageCount}
        />
      </HStack>
    </HStack>
  );
};
